import React, { FC, ReactElement, useContext, useEffect, useState } from 'react'
import { DirectoryModal as DirectoryModalLanguage } from '../../../../i18n'
import { getDirectoryByTelNumber } from '../../../NonReact/constants/ApiConstants'
// Utilities imports.
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { getPartitionInTelSysFromGUID } from '../../Common/API/ItemGetters'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
// Commons imports.
import { Modal } from '../../../ReactRouting/Components/Molecules/Modal/Modal'
import { ModalLoading } from '../../Common/Components/ModalLoading'
import { DocTopic } from '../../Common/Constants/Documentation'
// Contexts imports.
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    ReactState,
    UserInfoContextProvider
} from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import ModifyAddDirectoryTabs from '../DirectoryModal'
import { ApplicationDirectory, ApplicationDirectorysResponse } from '../Types/Types'
import { $window } from '../../../NonReact/common/AkixiWindow'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

type Props = {
    refreshAction: () => void
    filterTelSysID?: number
    filterPartitionID?: string
    /**
     * When set (partition ID in TelSys from admin filters), avoids GUID lookup via {@link getPartitionInTelSysFromGUID}.
     */
    filterPartitionPartIdInTelSys?: number
    selectedDirectory?: string
    viewOnly?: boolean
    /** Notified when the modal is closing so a host list can clear local open state and refresh. */
    onRequestClose?: () => void
}

type ModifyAddDirectoryContainerProps = {
    viewOnly?: boolean
    closeModal: () => void
    filterTelSysID?: string | number
    filterPartitionID?: string
    filterPartitionPartIdInTelSys?: number
    selectedDirectory?: string
    refreshAction: () => void
}

// Required for DirectoryInfo context loading.
const DirectoryTabsContainer: FC<ModifyAddDirectoryContainerProps> = (
    props: ModifyAddDirectoryContainerProps
): ReactElement => {
    const { displayError }: MessageContextType = useContext(MessageContext)
    const [partitionID, setPartitionID]: ReactState<Nullable<number>> = useState<Nullable<number>>(null)

    // selected directory info
    const [currentDirectory, setCurrentDirectory]: ReactState<Nullable<ApplicationDirectory>> =
        useState<Nullable<ApplicationDirectory>>(null)

    const {
        filterTelSysID,
        filterPartitionID,
        filterPartitionPartIdInTelSys,
        selectedDirectory,
        closeModal
    }: ModifyAddDirectoryContainerProps = props

    useEffect((): void => {
        if (selectedDirectory && !IsVarEmpty(filterTelSysID) && !IsVarEmpty(partitionID)) {
            // Fetch the currently selected directory.
            requestGetJSON<ApplicationDirectorysResponse>(
                getDirectoryByTelNumber(`${filterTelSysID}`, `${partitionID}`, selectedDirectory)
            )
                .then((response: JSONResponse<ApplicationDirectorysResponse>): void => {
                    const data: Definable<ApplicationDirectory> = response?.Data

                    if (!IsVarEmpty(data)) {
                        setCurrentDirectory(data)
                    }
                })
                .catch((e: Error) => {
                    displayError(e)
                    closeModal()
                })
        }
    }, [setCurrentDirectory, partitionID])

    useEffect((): void => {
        if (isVarNotEmpty(filterPartitionPartIdInTelSys) && filterPartitionPartIdInTelSys !== ALL_VALUE) {
            setPartitionID(Number(filterPartitionPartIdInTelSys))
            return
        }
        if (!IsVarEmpty(filterTelSysID) && filterTelSysID !== ALL_VALUE) {
            getPartitionInTelSysFromGUID(filterPartitionID)
                .then(setPartitionID)
                .catch((e: Error): void => {
                    displayError(e)
                    setPartitionID(ALL_VALUE)
                })
        } else {
            setPartitionID(ALL_VALUE)
        }
    }, [])

    if (!IsStringEmpty(selectedDirectory) && currentDirectory === null) {
        return <ModalLoading tabs={1} sections={4} />
    }

    return partitionID !== null ? (
        <ModifyAddDirectoryTabs
            {...props}
            selectedDirectory={selectedDirectory === null ? undefined : currentDirectory}
            filters={{
                telSysID: isVarNotEmpty(filterTelSysID) ? parseInt(`${filterTelSysID}`, 10) : ALL_VALUE,
                partitionID: partitionID ?? undefined
            }}
            viewOnly={props?.viewOnly}
        />
    ) : (
        <ModalLoading tabs={1} sections={4} />
    )
}

const DirectoryModal: FC<Props> = ({
    filterTelSysID,
    filterPartitionID,
    filterPartitionPartIdInTelSys,
    selectedDirectory,
    refreshAction,
    viewOnly,
    onRequestClose
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(DirectoryModalLanguage.NAME)

    // Whether the modal window is open.
    const [isModalOpen, setModalOpen]: ReactState<boolean> = useState<boolean>(true)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        onRequestClose?.()
        // Component is controlled externally.
        setModalOpen(false)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    isOpen={isModalOpen}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={{
                        message: language.get(DirectoryModalLanguage.DirectoryAddModify_PromptMessage),
                        confirmButtonText: language.get(DirectoryModalLanguage.DirectoryAddModify_PromptConfirm),
                        cancelButtonText: language.get(DirectoryModalLanguage.DirectoryAddModify_PromptCancel),
                        isRouterMode: true
                    }}
                    onOpen={(): void => {
                        // Add contextual doc topic ID to the window object which is used
                        // in old JSP logic that handles F1 key press.
                        setModalHelpTopicID(DocTopic.ADMINDIRECTORUADD)
                    }}
                    onClose={closeModal}
                    onExited={(): void => {
                        // Remove the contextual doc topic ID from the window that is used
                        // in old JSP logic that handles F1 key press.
                        // When removed the previous logic will work and the modal window doc topic will be not considered.
                        clearModalHelpTopic()
                    }}
                >
                    {isModalOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider>
                                <MessageContextProvider>
                                    <UserInfoContextProvider>
                                        <div className='modal-title-report-config'>
                                            {selectedDirectory
                                                ? language.get(DirectoryModalLanguage.ModifyDirectoryTitle)
                                                : language.get(DirectoryModalLanguage.AddDirectoryTitle)}
                                        </div>
                                        <div className='directory-add-modify'>
                                            <DirectoryTabsContainer
                                                filterTelSysID={filterTelSysID}
                                                filterPartitionID={filterPartitionID}
                                                filterPartitionPartIdInTelSys={filterPartitionPartIdInTelSys}
                                                selectedDirectory={selectedDirectory}
                                                viewOnly={viewOnly}
                                                closeModal={closeModal}
                                                refreshAction={refreshAction}
                                            />
                                        </div>
                                    </UserInfoContextProvider>
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <> </>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}

export default DirectoryModal
